<template>
	<div
		class="filters-section--wrapper"
		:class="{mobile: isMobileWebApp, desktop: isDesktopWebApp }"
	>
		<div class="filters-section--header">
			<div class="filters-section--title">
				{{ title }}
			</div>
			<div class="filters-section--caret">
				<Icon
					v-if="!!options.length"
					icon-name="down-arrow"
					:class="{ 'open': isOpen }"
					size="lg"
					@click="toggleDisplay"
				/>
			</div>
		</div>
		<slot name="subheader" />
		<div
			v-if="options.length"
			v-dragscroll="!isMobileWebApp && !isOpen"
			class="filters-section--list"
			:class="{'is-open': isOpen, 'is-dragging': isDragging}"
			@dragscrollstart="handleDragStart"
			@dragscrollend="handleDragEnd"
		>
			<div
				v-for="(option, key) in options"
				:key="key"
				class="filers-section--badge"
				:class="{selected: option.isSelected, disabled: option.isDisabled }"
				@click="onClick(option)"
			>
				<template v-if="isRatingFilter">
					<div class="filers-section--icon-w">
						<Icon
							v-for="i in parseInt(option.clipFilter.label)"
							:key="i"
							size="sm"
							icon-name="star-filled"
						/>
					</div>
					<span>({{ option.clipFilter.count }})</span>
				</template>
				<template v-else>
					<span>{{ option.clipFilter.label }}</span>
					<span>({{ option.clipFilter.count }})</span>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import { FILTER_GROUP_RATING } from '@videotools/js/store/modules/clip-filters';
import { mapGetters } from 'vuex';

export default {
	name:  'FiltersListSection',
	props: {
	  title: {
	    type:     String,
			required: true,
		},
		clipFilters: {
	    	type:     Array,
			required: true,
		},
		value: {
			type: Array,
		},
		hideDisabled: {
			type: Boolean,
		},
		initiallyOpen: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			isOpen:     false,
			isDragging: false,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:  'app/isMobileWebApp',
			isDesktopWebApp: 'app/isDesktopWebApp',
		}),
		options() {
			const ret = this.clipFilters
				.map((clipFilter) => ({
					id:         clipFilter.id,
					isSelected: !!this.value.find((selClipFilter) => selClipFilter.key === clipFilter.key),
					clipFilter: clipFilter,
					isDisabled: clipFilter.count === 0,
				}))
				.sort((optionA, optionB) => {
					if (optionA.clipFilter.type === FILTER_GROUP_RATING) {
						// sort by stars in optionA/B.clipFilter.label
						if (optionA.clipFilter.label === optionB.clipFilter.label) {
							return 0;
						}
						return optionA.clipFilter.label < optionB.clipFilter.label ? -1 : 1;
					}
					if (optionA.clipFilter.count === optionB.clipFilter.count) {
						return 0;
					}
					return optionA.clipFilter.count < optionB.clipFilter.count ? 1 : -1;
				});

			if (this.hideDisabled) {
				return ret.filter(option => !option.isDisabled);
			}

			return ret;
		},
		isRatingFilter() {
			return this.clipFilters.some((clipFilter) => clipFilter.type === FILTER_GROUP_RATING);
		},
	},
	watch: {
		dataValue(val) {
			this.$emit('input', val);
		},
	},
	created() {
		this.isOpen = this.initiallyOpen;
	},
	methods: {
		toggleDisplay() {
			this.isOpen = !this.isOpen;
		},
		onClick(option) {
		  if (this.isDragging) {
				return false;
	 		}

			if (option.isDisabled) {
		    	return;
			}

			if (this.value.find(clipFilter => clipFilter.key === option.clipFilter.key)) {
				this.$emit(
					'input',
					this.value.filter((clipFilter) => clipFilter.key !== option.clipFilter.key)
				);
			} else {
				this.$emit(
					'input',
					[...this.value, option.clipFilter]
				);
			}
		},
		handleDragStart() {
		  setTimeout(() => this.isDragging = true, 120);
		},
		handleDragEnd() {
		  setTimeout(() => this.isDragging = false, 60);
		},
	},

};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/_fonts';

.filters-section--wrapper {
	margin-bottom: 40px;

	&.mobile {
		.filters-section--title {
			@include text-md-1;
		}

		.subheader {
			@include text-md-1;
		}
	}
}

.filters-section--header {
	display: flex;
	justify-content: space-between;
}

.filters-section--title {
  	@include text-md-2;
	font-weight: bold;
	margin-bottom: 16px;
}

.filters-section--list {
	display: flex;
	overflow-x: auto;
	margin-right: -24px;

	.is-mobile & {
		margin-right: -14px;
	}

	&.is-dragging {
		cursor: grabbing;
	}

	&.is-open {
		overflow-x: initial;
		flex-wrap: wrap;
	}
}

.filers-section--badge {
	@include text-sm-2;

	display: flex;
	align-items: center;
	gap: 4px;
	padding: 6px 16px;
	color: var(--color-grey-300);
	white-space: nowrap;
	border: 1px solid var(--color-grey-300);
	box-sizing: border-box;
	cursor: pointer;

	.is-dragging & {
		cursor: grabbing;
  }

	border-radius: 24px;
	margin-right: 8px;
	margin-bottom: 16px;

	.is-mobile & {
		@include text-md-1;
		padding: 8px 16px;
	}

	.is-desktop &:not(.disabled) {
		&:hover {
			background-color: var(--color-grey-700);
		}
	}

	&.selected:not(.disabled) {
		background-color: var(--color-hype-yellow);
		color: var(--color-text-on-hype-yellow);
		border: 1px solid var(--color-hype-yellow);

		.is-desktop & {
			&:hover {
				background-color: var(--color-hype-yellow--active);
			}
		}
	}

	&.disabled {
		color: var(--color-grey-600);
		border-color: var(--color-grey-600);
	}
}

.filers-section--icon-w {
	display: flex;
}

.filters-section--caret {
  .icon,
  .v2-icon {
		transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) 0s;

		&.open {
			transform: rotate(180deg);
		}
  }
}

/* width */
::-webkit-scrollbar {
  width: 2px;
}

/* Track */
::-webkit-scrollbar-track {
  border-radius: 2px;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: var(--color-grey-850);
  border-radius: 2px;
}

</style>
